import { ChevronDownIcon } from "@chakra-ui/icons";
import { Flex, Input, InputProps, Menu, MenuButton, useDisclosure } from "@chakra-ui/react";
import { useEffect, useId, useRef, useState } from "react";

import { useAnimationFrame } from "@nens/react/hooks";

import { CancelIcon } from "../../../icons/cancelIcon";
import { useKeyNavigation } from "../hooks/use-key-navigation";
import { MaxItemsState, useMaxItems } from "../hooks/use-max-items";

import { RanaSelectMenuList } from "./rana-select-menu-list";

export interface RanaSelectSearchProps extends Omit<InputProps, "onChange" | "value"> {
  options: { value: string }[];
  onChange: (value: string | undefined) => void;
  value: string | undefined;
  searchState: [string, (searchString: string) => void];
  showDropdownOnEmptySearch?: boolean;
  isReadOnly?: boolean;
  maxItemsState?: MaxItemsState;
}

export function RanaSelectSearch(props: RanaSelectSearchProps) {
  const {
    options,
    onChange,
    isReadOnly,
    value,
    searchState,
    maxItemsState,
    showDropdownOnEmptySearch,
    ...rest
  } = props;
  const { maxItems, initialMaxItems, increaseMaxItems, resetMaxItems } = useMaxItems(maxItemsState);

  const [isSearching, setIsSearching] = useState(false);
  const [searchString, _setSearchString] = searchState;
  const setSearchString: typeof _setSearchString = (s) => {
    resetMaxItems();
    _setSearchString(s);
  };
  const { isOpen, onOpen, onClose } = useDisclosure();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const id = useId();

  useEffect(() => {
    if (value) setIsSearching(false);
  }, [value]);

  const ensureFocus = () => {
    if (inputRef.current === null) return;
    if (isOpen && document.activeElement !== inputRef.current) {
      inputRef.current.focus();
    }
    if (!isOpen && document.activeElement === inputRef.current) {
      if (isSearching) {
        setIsSearching(false);
      }
      if (searchString) {
        setSearchString("");
      }
    }
  };

  useAnimationFrame(ensureFocus, 60);

  const { onKeyDown, selectedIdx } = useKeyNavigation({
    onChange,
    onClose,
    options,
    searchString,
    maxItems,
    increaseMaxItems,
  });

  const startSearching = () => {
    onOpen();
    if (!isSearching) {
      setIsSearching(true);
      if (!searchString && value) setSearchString(value);
    }
  };

  const cancel = () => {
    onChange(undefined);
    setSearchString("");
    setIsSearching(false);
  };

  const onSearchValueChanged = (newValue: string) => {
    onOpen();
    setSearchString(newValue);
    setIsSearching(true);
  };

  return (
    <Flex w="full" ref={wrapperRef} position="relative">
      <Input
        bg="brand.grey.100"
        w="full"
        id={id}
        ref={inputRef}
        type="text"
        value={isSearching ? searchString : value}
        onClick={!isReadOnly ? startSearching : undefined}
        onChange={(e) => onSearchValueChanged(e.target.value)}
        isReadOnly={isReadOnly}
        onKeyDown={onKeyDown}
        {...rest}
      />

      {!isReadOnly && (
        <ChevronDownIcon
          zIndex={1}
          position="absolute"
          top="1/2"
          transform="translateY(-50%)"
          right={2}
          pointerEvents="none"
        />
      )}

      {value && !isReadOnly && (
        <Flex
          bg="brand.grey.100"
          w="fit"
          position="absolute"
          color="brand.analytic"
          right={1}
          top="1/2"
          transform="translateY(-50%)"
          p={1}
          zIndex={9999}
          onClick={cancel}
        >
          <CancelIcon onClick={cancel} cursor="pointer" />
        </Flex>
      )}

      <Menu onClose={onClose} isOpen={isOpen} isLazy>
        <MenuButton opacity={0} pointerEvents="none" w={0} h={0} position="absolute" left={0} />

        {isSearching && (showDropdownOnEmptySearch || searchString) && (
          <RanaSelectMenuList
            minW={`${wrapperRef.current?.clientWidth ?? 0}px`}
            value={value}
            options={options.map(({ value }) => ({ value, label: value }))}
            selectedIdx={selectedIdx}
            searchString={searchString}
            onChange={onChange}
            increaseMaxItems={increaseMaxItems}
            initialMaxItems={initialMaxItems}
            maxItems={maxItems}
          />
        )}
      </Menu>
    </Flex>
  );
}
